import * as Yup from 'yup'
import {
  identificadoresShape,
  tipoDocumentoEmUso,
} from 'app/modules/apps/shared/documentos-pessoa/identificadores'
import { apenasDigitos, createValidation } from '../../../../../../../../_metronic/helpers'
import { TFunction } from '_metronic/i18n/Metronici18n'
import { resolverCampoFormik, satisfeitoPorFci } from '../../../core/_fciCampos'
import {
  camposExigidosAgora,
  ContextoCondicaoFci,
  RegrasCadastroPacienteFci,
} from '../../../core/_fciRegras'

/**
 * Marca o campo como obrigatório usando um **teste nomeado**, e não `.required()`.
 *
 * Motivo técnico: vários campos deste formulário já são condicionais por `when(...)`, e no yup 0.32
 * o ramo `otherwise` é concatenado sobre o schema base — um `.required()` posto antes seria apagado
 * por um `notRequired()` do ramo. Teste com nome próprio (`fci-obrigatorio`) não colide com nada.
 */
const exigir = (schema: any, mensagem: string) =>
  schema.test('fci-obrigatorio', mensagem, (valor: any) => {
    if (valor === undefined || valor === null) return false
    // Combos guardam a opção inteira (`{label, value}`): o que conta é o `value`.
    if (typeof valor === 'object') {
      const selecionado = (valor as { value?: unknown }).value
      return selecionado !== undefined && selecionado !== null && selecionado !== ''
    }
    return String(valor).trim() !== ''
  })

/**
 * Aplica ao shape a obrigatoriedade **lida** do endpoint da F3.
 *
 * NENHUMA regra do LEDI mora neste arquivo: os campos vêm de `camposObrigatorios[].campo`, as
 * condições são avaliadas por `camposExigidosAgora` e a mensagem é a `correcao` que o backend
 * escreveu. Mudar a tabela do LEDI não obriga a mexer aqui.
 */
const aplicarObrigatoriedadeFci = (
  shape: Record<string, any>,
  values: any,
  regras?: RegrasCadastroPacienteFci,
  contexto: ContextoCondicaoFci = {}
) => {
  camposExigidosAgora(regras, contexto).forEach(({ campo, correcao }) => {
    const jaSatisfeito = satisfeitoPorFci(campo)
    if (jaSatisfeito?.(values)) return

    const campoFormik = resolverCampoFormik(campo, { tipoDocumento: tipoDocumentoEmUso(values) })
    if (!campoFormik || !shape[campoFormik]) return

    shape[campoFormik] = exigir(shape[campoFormik], correcao)
  })
}

/**
 * @param regras resposta de `GET /empresas/{id}/esus/cadastro-paciente`; ausente ou com
 *   `esusAtivo: false` deixa o formulário exatamente como era antes do e-SUS.
 * @param resolverContexto lê do estado da tela o que as condições observam (código LEDI da
 *   nacionalidade, raça, ausência de CPF). Recebe os valores atuais porque a obrigatoriedade é
 *   condicional — daí o `Yup.lazy`.
 */
export const editPacienteSchema = (
  t: TFunction,
  showEndereco: boolean,
  showAso: boolean,
  emEdicao?: boolean,
  cpfOriginal?: string,
  cnsOriginal?: string,
  regras?: RegrasCadastroPacienteFci,
  resolverContexto?: (values: any) => ContextoCondicaoFci,
  documentoEstrangeiroNaLista: boolean = false
) => {
  const v = createValidation(t)

  return Yup.lazy((values: any) => {
    const shape: Record<string, any> = montarShape(
      t,
      v,
      showEndereco,
      showAso,
      emEdicao,
      cpfOriginal,
      cnsOriginal,
      documentoEstrangeiroNaLista
    )
    aplicarObrigatoriedadeFci(shape, values, regras, resolverContexto?.(values) ?? {})
    return Yup.object().shape(shape)
  })
}

const montarShape = (
  t: TFunction,
  v: ReturnType<typeof createValidation>,
  showEndereco: boolean,
  showAso: boolean,
  emEdicao?: boolean,
  cpfOriginal?: string,
  cnsOriginal?: string,
  documentoEstrangeiroNaLista: boolean = false
): Record<string, any> => {
  return {
    empresa: Yup.object().nullable().required(v.requiredField('empresa', 'select')),
    sexo: Yup.object().nullable().required('Selecione um sexo'),

    id: Yup.number().notRequired(),
    primeiroNome: Yup.string()
      .min(3, 'Minimo de 3 caracteres')
      .max(50, 'Máximo de 50 caracteres')
      .trim()
      .required('O campo nome é obrigatório'),
    sobreNome: Yup.string()
      .min(3, 'Minimo de 3 caracteres')
      .max(50, 'Máximo de 50 caracteres')
      .trim()
      .required('O campo sobrenome é obrigatório'),
    ...identificadoresShape(t, documentoEstrangeiroNaLista),
    justificativa: Yup.string().when(['cpf', 'cns'], {
      is: (cpf?: string, cns?: string) =>
        emEdicao &&
        (apenasDigitos(cpf) !== apenasDigitos(cpfOriginal) ||
          apenasDigitos(cns) !== apenasDigitos(cnsOriginal)),
      then: (schema) =>
        schema.required('A justificativa é obrigatória quando o CPF/CNS é alterado'),
    }),
    email: Yup.string().email('Formato inválido').notRequired().nullable(),
    nascimento: Yup.string().required('O campo data de nascimento é obrigatório'),

    nomeSocial: Yup.string().trim().notRequired(),
    nomeMae: Yup.string().trim().notRequired(),
    nomeRepresentante: Yup.string().trim().notRequired(),
    raca: Yup.object().nullable().notRequired(),
    genero: Yup.object().nullable().notRequired(),
    nacionalidade: Yup.object().nullable().notRequired(),
    estadoNascimento: Yup.object().nullable().notRequired(),

    // Campos da Ficha de Cadastro Individual do e-SUS APS. Todos **opcionais por padrão**: a
    // obrigatoriedade é ligada por `aplicarObrigatoriedadeFci` a partir da resposta do endpoint da
    // F3, nunca escrita aqui.
    desconheceNomeMae: Yup.boolean().notRequired(),
    telefoneCelular: Yup.string()
      .matches(
        /^\(\d{2}\) (\d )?\d{4}-\d{4}$/,
        'Formato deve ser (00) 0000-0000 ou (00) 0 0000-0000'
      )
      .max(20, 'Máximo de 20 caracteres')
      .notRequired()
      .nullable(),
    municipioNascimento: Yup.object().nullable().notRequired(),
    justificativaNaoPossuiCpf: Yup.object().nullable().notRequired(),
    etnia: Yup.object().nullable().notRequired(),
    dtNaturalizacao: Yup.string().notRequired().nullable(),
    portariaNaturalizacao: Yup.string().trim().max(100, 'Máximo de 100 caracteres').notRequired(),
    paisNascimento: Yup.object().nullable().notRequired(),
    dtEntradaBrasil: Yup.string().notRequired().nullable(),
    peso: Yup.string().trim().notRequired(),
    altura: Yup.string().trim().notRequired(),
    condicoes: Yup.string().trim().notRequired(),

    alergias: Yup.array()
      .of(
        Yup.object({
          descricao: Yup.string().trim().required('Necessário inserir descrição'),
        })
      )
      .default([]),
    doencas: Yup.array()
      .of(
        Yup.object({
          descricao: Yup.string().trim().required('Necessário inserir descrição'),
        })
      )
      .default([]),
    medicamentos: Yup.array()
      .of(
        Yup.object({
          descricao: Yup.string().trim().required('Necessário inserir descrição'),
        })
      )
      .default([]),

    rg: Yup.object({
      numero: Yup.string().when([], {
        is: () => showAso,
        then: (schema) => schema.required('O campo número é obrigatório'),
        otherwise: (schema) => schema.notRequired(),
      }),
      orgao: Yup.string().when([], {
        is: () => showAso,
        then: (schema) => schema.required('O campo órgão é obrigatório'),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),

    ctps: Yup.object({
      numero: Yup.string().when([], {
        is: () => showAso,
        then: (schema) => schema.required('O campo número é obrigatório'),
        otherwise: (schema) => schema.notRequired(),
      }),

      serie: Yup.string().when([], {
        is: () => showAso,
        then: (schema) => schema.required('O campo série é obrigatório'),
        otherwise: (schema) => schema.notRequired(),
      }),

      uf: Yup.object()
        .nullable()
        .when([], {
          is: () => showAso,
          then: (schema) => schema.required('O campo UF é obrigatório'),
          otherwise: (schema) => schema.notRequired(),
        }),
    }),

    dadosAso: Yup.object({
      cargo: Yup.string().when([], {
        is: () => showAso,
        then: (schema) => schema.required('O campo cargo é obrigatório'),
        otherwise: (schema) => schema.notRequired(),
      }),

      setor: Yup.string().when([], {
        is: () => showAso,
        then: (schema) => schema.required('O campo setor é obrigatório'),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),

    endereco: Yup.object({
      tipoEndereco: Yup.string().notRequired(),

      cep: Yup.string()
        .matches(/^\d{2}\.\d{3}-\d{3}$/, 'Formato deve ser 00.000-000')
        .when([], {
          is: () => showEndereco,
          then: (schema) => schema.required('O campo CEP é obrigatório'),
          otherwise: (schema) => schema.notRequired(),
        }),

      logradouro: Yup.string().when([], {
        is: () => showEndereco,
        then: (schema) =>
          schema
            .min(5, 'Minimo de 5 caracteres')
            .max(50, 'Máximo de 50 caracteres')
            .required('O campo logradouro é obrigatório'),
        otherwise: (schema) => schema.notRequired(),
      }),

      numero: Yup.string().when([], {
        is: () => showEndereco,
        then: (schema) => schema.required('O campo número é obrigatório'),
        otherwise: (schema) => schema.notRequired(),
      }),

      complemento: Yup.string().notRequired(),

      bairro: Yup.string().when([], {
        is: () => showEndereco,
        then: (schema) =>
          schema
            .min(3, 'Minimo de 3 caracteres')
            .max(50, 'Máximo de 50 caracteres')
            .required('O campo bairro é obrigatório'),
        otherwise: (schema) => schema.notRequired(),
      }),

      cidade: Yup.string().when([], {
        is: () => showEndereco,
        then: (schema) =>
          schema
            .min(3, 'Minimo de 3 caracteres')
            .max(50, 'Máximo de 50 caracteres')
            .required('O campo cidade é obrigatório'),
        otherwise: (schema) => schema.notRequired(),
      }),

      uf: Yup.object()
        .nullable()
        .when([], {
          is: () => showEndereco,
          then: (schema) => schema.required('O campo UF é obrigatório'),
          otherwise: (schema) => schema.notRequired(),
        }),
    }),
  }
}
